import React, { useState, useEffect } from 'react';
import {
  Box,
  Divider,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  CloudSync as CloudSyncIcon
} from '@mui/icons-material';
import api from '../services/api';
import { sectionsForPosition, slugFromRecord, humanLabel, POSITION_TITLES } from '../utils/recruitmentFields';

// Render one raw answer: arrays -> comma list, URLs -> links, else text.
const renderAnswer = (val) => {
  if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) return null;
  if (Array.isArray(val)) return val.join(', ');
  const s = String(val);
  if (/^https?:\/\//i.test(s)) return <a href={s} target="_blank" rel="noopener noreferrer">{s}</a>;
  return s;
};

// Grouped, sectioned view of a portal application. Shows the COMPLETE field
// set for the applicant's position (a faithful mirror of the portal form) —
// answered values filled in, unanswered fields shown as "—".
const PortalApplicationDetails = ({ record }) => {
  const data = record?.externalData?.data;
  if (!data || typeof data !== 'object') return null;
  const slug = slugFromRecord(record);
  const sections = sectionsForPosition(slug);
  const covered = new Set();
  const blocks = sections.map((sec) => {
    const rows = sec.fields.map((key) => {
      covered.add(key);
      return { key, node: renderAnswer(data[key]) };
    });
    return { title: sec.title, rows };
  });

  // Any submitted answers not covered by the schema (forward-compatible).
  const extras = Object.keys(data)
    .filter((k) => !covered.has(k) && renderAnswer(data[k]) !== null && !/^photo\d?$/i.test(k) && k !== 'role')
    .map((k) => ({ key: k, node: renderAnswer(data[k]) }));
  if (extras.length) blocks.push({ title: 'Other', rows: extras });

  return (
    <Grid item xs={12}>
      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
        Application from recruit.jbdgobs.com{slug ? ` — ${POSITION_TITLES[slug]}` : ''}
      </Typography>
      {blocks.map((b) => (
        <Box key={b.title} sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>{b.title}</Typography>
          <Grid container spacing={1} sx={{ mt: 0.2 }}>
            {b.rows.map((r) => (
              <Grid item xs={12} sm={6} key={r.key}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>{humanLabel(r.key)}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', color: r.node === null ? 'text.disabled' : 'text.primary' }}>
                  {r.node === null ? '—' : r.node}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Grid>
  );
};

const RecruitmentManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [recruitments, setRecruitments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRecruitment, setSelectedRecruitment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [formError, setFormError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState({ open: false, severity: 'success', message: '' });
  
  // Dropdown data
  const [positions, setPositions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);

  const [formData, setFormData] = useState({
    fullName: '',
    contactNumber: '',
    email: '',
    age: '',
    dateOfBirth: '',
    position: '',
    expectedSalary: '',
    approvedSalary: '',
    status: 'Technical/Audition',
    dateOfJoining: '',
    address: '',
    qualification: '',
    experienceYears: '',
    skillsAndExperience: '',
    portfolio: '',
    interviewDate: '',
    interviewerName: '',
    interviewFeedback: '',
    technicalRating: '',
    communicationRating: '',
    overallRating: '',
    referenceName: '',
    referenceContact: '',
    referenceRelation: '',
    applicationDate: new Date().toISOString().split('T')[0],
    sourceOfApplication: ''
  });

  useEffect(() => {
    fetchRecruitments();
    fetchStats();
    fetchDropdowns();
  }, [page, rowsPerPage, filterStatus, filterPosition, searchTerm]);

  const fetchDropdowns = async () => {
    try {
      console.log('Fetching dropdowns...');
      const response = await api.get('/dropdowns');
      console.log('Full API Response:', response.data);
      const dropdownsData = response.data.data;
      console.log('Dropdown Data:', dropdownsData);
      
      // The API returns an object with category names as keys
      // Each category contains an array of values
      const positionValues = dropdownsData.recruitment_position || [];
      const statusValues = dropdownsData.recruitment_status || [];
      const sourceValues = dropdownsData.recruitment_source || [];
      
      console.log('Position Values:', positionValues);
      console.log('Status Values:', statusValues);
      console.log('Source Values:', sourceValues);
      
      // Convert arrays of strings to objects with value property
      const positionsArray = positionValues.map((val, idx) => ({ id: idx, value: val }));
      const statusesArray = statusValues.map((val, idx) => ({ id: idx, value: val }));
      const sourcesArray = sourceValues.map((val, idx) => ({ id: idx, value: val }));
      
      console.log('Mapped Positions:', positionsArray);
      console.log('Mapped Statuses:', statusesArray);
      console.log('Mapped Sources:', sourcesArray);
      
      setPositions(positionsArray);
      setStatuses(statusesArray);
      setSources(sourcesArray);
      
      console.log('Dropdowns set successfully');
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const fetchRecruitments = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterStatus) params.status = filterStatus;
      if (filterPosition) params.position = filterPosition;

      const response = await api.get('/recruitment', { params });
      setRecruitments(response.data.data);
      setTotalCount(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching recruitments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/recruitment/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenDialog = (recruitment = null) => {
    setFormError('');
    setFormErrors({});
    if (recruitment) {
      setSelectedRecruitment(recruitment);
      // Combine skills and experience when loading for editing
      const combinedSkillsExperience = recruitment.skills || recruitment.experience || '';
      setFormData({
        ...recruitment,
        skillsAndExperience: combinedSkillsExperience
      });
    } else {
      setSelectedRecruitment(null);
      setFormData({
        fullName: '',
        contactNumber: '',
        email: '',
        age: '',
        dateOfBirth: '',
        position: '',
        expectedSalary: '',
        approvedSalary: '',
        status: 'Technical/Audition',
        dateOfJoining: '',
        address: '',
        qualification: '',
        experienceYears: '',
        skillsAndExperience: '',
        portfolio: '',
        interviewDate: '',
        interviewerName: '',
        interviewFeedback: '',
        technicalRating: '',
        communicationRating: '',
        overallRating: '',
        referenceName: '',
        referenceContact: '',
        referenceRelation: '',
        applicationDate: new Date().toISOString().split('T')[0],
        sourceOfApplication: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRecruitment(null);
    setFormError('');
    setFormErrors({});
  };

  const handleViewDetails = (recruitment) => {
    setSelectedRecruitment(recruitment);
    setOpenViewDialog(true);
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setFormError('');
    setFormErrors({});
    
    // Frontend validation - only Name, Contact, Age, DOB are mandatory
    const requiredFields = [
      { field: 'fullName', label: 'Full Name' },
      { field: 'contactNumber', label: 'Contact Number' },
      { field: 'age', label: 'Age' },
      { field: 'dateOfBirth', label: 'Date of Birth' }
    ];
    
    const errors = {};
    const missingFields = [];
    
    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field] === '') {
        errors[field] = `${label} is required`;
        missingFields.push(label);
      }
    }
    
    if (missingFields.length > 0) {
      setFormErrors(errors);
      setFormError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    try {
      if (selectedRecruitment) {
        await api.put(`/recruitment/${selectedRecruitment.id}`, formData);
      } else {
        await api.post('/recruitment', formData);
      }
      fetchRecruitments();
      fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving recruitment:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Failed to save recruitment record';
      
      if (error.response?.data?.error) {
        const errorStr = error.response.data.error;
        
        // Parse validation errors
        if (errorStr.includes('Validation isEmail')) {
          errorMessage = 'Invalid email format. Please enter a valid email address (e.g., user@example.com)';
          setFormErrors({ email: 'Invalid email format' });
        } else if (errorStr.includes('invalid input syntax for type date')) {
          errorMessage = 'Invalid date format. Please enter a valid date.';
        } else if (errorStr.includes('invalid input syntax for type numeric')) {
          errorMessage = 'Invalid number format. Please enter a valid number.';
        } else if (errorStr.includes('Missing required fields')) {
          errorMessage = errorStr;
        } else {
          errorMessage = errorStr;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setFormError(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this recruitment record?')) {
      try {
        await api.delete(`/recruitment/${id}`);
        fetchRecruitments();
        fetchStats();
      } catch (error) {
        console.error('Error deleting recruitment:', error);
        
        // Extract meaningful error message
        let errorMessage = 'Error deleting recruitment record';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(errorMessage);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Technical/Audition': 'info',
      'HR Discussion': 'warning',
      'Final Round': 'secondary',
      'Selected': 'success',
      'Rejected': 'error',
      'On Hold': 'default',
      'Offer Extended': 'primary',
      'Joined': 'success',
      'Onboard': 'primary'
    };
    return colors[status] || 'default';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manually pull applications submitted on the recruitment portal.
  // Applications also arrive automatically (webhook + 15-min catch-up sync);
  // this is for when you don't want to wait.
  const handlePullFromPortal = async () => {
    setPulling(true);
    try {
      const res = await api.post('/integrations/recruit/sync', {});
      const d = res.data?.data || {};
      const nothingNew = !d.created && !d.updated && !d.failed;
      setPullResult({
        open: true,
        severity: d.failed ? 'warning' : 'success',
        message: nothingNew
          ? 'No new applications on the portal.'
          : `${d.created || 0} new, ${d.updated || 0} updated${d.failed ? `, ${d.failed} failed` : ''}.`,
      });
      fetchRecruitments();
      fetchStats();
    } catch (error) {
      setPullResult({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Could not reach the recruitment portal.',
      });
    } finally {
      setPulling(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {isMobile ? 'Recruitment Mgmt' : 'Recruitment Management'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Fetch new applications submitted on recruit.jbdgobs.com">
            <span>
              <Button
                variant="outlined"
                startIcon={pulling ? <CircularProgress size={16} /> : (!isMobile && <CloudSyncIcon />)}
                onClick={handlePullFromPortal}
                disabled={pulling}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Pull' : (pulling ? 'Pulling…' : 'Pull from JBDGOBS')}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Candidate'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={pullResult.open}
        autoHideDuration={6000}
        onClose={() => setPullResult((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={pullResult.severity}
          onClose={() => setPullResult((p) => ({ ...p, open: false }))}
        >
          {pullResult.message}
        </Alert>
      </Snackbar>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4} sm={4} md={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                      Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
                      {stats.total}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: { xs: 32, md: 48 }, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                      Selected
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.5rem' } }} color="success.main">
                      {stats.byStatus?.find(s => s.status === 'Selected')?.count || 0}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 48 }, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                      In Progress
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.5rem' } }} color="info.main">
                      {stats.byStatus?.filter(s => 
                        ['Technical/Audition', 'HR Discussion', 'Final Round'].includes(s.status)
                      ).reduce((sum, s) => sum + s.count, 0) || 0}
                    </Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: { xs: 32, md: 48 }, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, contact, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.value}>
                    {status.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Position</InputLabel>
              <Select
                value={filterPosition}
                onChange={(e) => setFilterPosition(e.target.value)}
                label="Filter by Position"
              >
                <MenuItem value="">All Positions</MenuItem>
                {positions.map((position) => (
                  <MenuItem key={position.id} value={position.value}>
                    {position.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setFilterStatus('');
                setFilterPosition('');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Candidates List - Mobile: Cards, Desktop: Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* Mobile Card View */
        <Box>
          <Grid container spacing={2}>
            {recruitments.map((recruitment) => (
              <Grid item xs={12} key={recruitment.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                  onClick={() => handleViewDetails(recruitment)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {recruitment.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {recruitment.position || 'Position not specified'}
                        </Typography>
                      </Box>
                      <Chip
                        label={recruitment.status}
                        color={getStatusColor(recruitment.status)}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {recruitment.contactNumber}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(recruitment.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {recruitments.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No recruitment records found
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expected Salary</TableCell>
                <TableCell>Approved Salary</TableCell>
                <TableCell>Application Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recruitments.map((recruitment) => (
                <TableRow key={recruitment.id} hover onClick={() => handleViewDetails(recruitment)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{recruitment.fullName}</TableCell>
                  <TableCell>{recruitment.contactNumber}</TableCell>
                  <TableCell>{recruitment.position}</TableCell>
                  <TableCell>
                    <Chip
                      label={recruitment.status}
                      color={getStatusColor(recruitment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {recruitment.expectedSalary ? `₹${parseFloat(recruitment.expectedSalary).toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell>
                    {recruitment.approvedSalary ? `₹${parseFloat(recruitment.approvedSalary).toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(recruitment.applicationDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(recruitment)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(recruitment)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(recruitment.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {recruitments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No recruitment records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedRecruitment ? 'Edit Candidate' : 'Add New Candidate'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                required
                value={formData.contactNumber}
                onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                error={!!formErrors.contactNumber}
                helperText={formErrors.contactNumber}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                required
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                error={!!formErrors.age}
                helperText={formErrors.age}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                error={!!formErrors.dateOfBirth}
                helperText={formErrors.dateOfBirth}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Application Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.applicationDate}
                onChange={(e) => setFormData({ ...formData, applicationDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Application Source</InputLabel>
                <Select
                  value={formData.sourceOfApplication}
                  onChange={(e) => setFormData({ ...formData, sourceOfApplication: e.target.value })}
                  label="Application Source"
                >
                {sources.map((source) => (
                  <MenuItem key={source.id} value={source.value}>
                    {source.value}
                  </MenuItem>
                ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Complete Address"
                multiline
                rows={2}
                placeholder="Enter complete address with city, state, pincode..."
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            {/* Experience & Qualifications */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Experience & Qualifications
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Qualification"
                value={formData.qualification}
                onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={formData.experienceYears}
                onChange={(e) => setFormData({ ...formData, experienceYears: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Skills & Experience Details"
                multiline
                rows={3}
                placeholder="Describe skills, experience, and relevant background..."
                value={formData.skillsAndExperience}
                onChange={(e) => setFormData({ ...formData, skillsAndExperience: e.target.value })}
              />
            </Grid>

            {/* Position & Status */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Position & Status
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  label="Position"
                >
                  {positions.map((position) => (
                    <MenuItem key={position.id} value={position.value}>
                      {position.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.id} value={status.value}>
                      {status.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Expected Salary"
                type="number"
                value={formData.expectedSalary}
                onChange={(e) => setFormData({ ...formData, expectedSalary: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Approved Salary"
                type="number"
                value={formData.approvedSalary}
                onChange={(e) => setFormData({ ...formData, approvedSalary: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date of Joining"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfJoining}
                onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
              />
            </Grid>

            {/* Interview Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Interview Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Interview Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.interviewDate}
                onChange={(e) => setFormData({ ...formData, interviewDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Interviewer Name"
                value={formData.interviewerName}
                onChange={(e) => setFormData({ ...formData, interviewerName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Technical Rating (1-10)"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={formData.technicalRating}
                onChange={(e) => setFormData({ ...formData, technicalRating: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Communication Rating (1-10)"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={formData.communicationRating}
                onChange={(e) => setFormData({ ...formData, communicationRating: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Overall Rating (1-10)"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={formData.overallRating}
                onChange={(e) => setFormData({ ...formData, overallRating: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interview Feedback"
                multiline
                rows={2}
                value={formData.interviewFeedback}
                onChange={(e) => setFormData({ ...formData, interviewFeedback: e.target.value })}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {selectedRecruitment ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Candidate Details</DialogTitle>
        <DialogContent>
          {selectedRecruitment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Status:</strong> {selectedRecruitment.status}
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Full Name</Typography>
                <Typography variant="body1">{selectedRecruitment.fullName}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Position</Typography>
                <Typography variant="body1">{selectedRecruitment.position}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Contact Number</Typography>
                <Typography variant="body1">{selectedRecruitment.contactNumber}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{selectedRecruitment.email || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Age / DOB</Typography>
                <Typography variant="body1">
                  {selectedRecruitment.age} years / {new Date(selectedRecruitment.dateOfBirth).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Experience</Typography>
                <Typography variant="body1">{selectedRecruitment.experienceYears || '-'} years</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Expected Salary</Typography>
                <Typography variant="body1">
                  {selectedRecruitment.expectedSalary ? `₹${parseFloat(selectedRecruitment.expectedSalary).toLocaleString()}` : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Approved Salary</Typography>
                <Typography variant="body1">
                  {selectedRecruitment.approvedSalary ? `₹${parseFloat(selectedRecruitment.approvedSalary).toLocaleString()}` : '-'}
                </Typography>
              </Grid>
              {selectedRecruitment.skills && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Skills</Typography>
                  <Typography variant="body1">{selectedRecruitment.skills}</Typography>
                </Grid>
              )}
              {selectedRecruitment.interviewFeedback && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Interview Feedback</Typography>
                  <Typography variant="body1">{selectedRecruitment.interviewFeedback}</Typography>
                </Grid>
              )}
              {selectedRecruitment.remarks && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Remarks</Typography>
                  <Typography variant="body1">{selectedRecruitment.remarks}</Typography>
                </Grid>
              )}
              <PortalApplicationDetails record={selectedRecruitment} />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecruitmentManagement;